import { Flag, ListTodo, Mail } from "lucide-react";
import { Alert } from "@/components/common/Alert/Alert";
import { LoadingPage } from "@/components/common/Loading/LoadingPage";
import { MaintenanceSection, MaintenanceSectionContent, MaintenanceSectionHeader } from "@/components/layout/maintenance";
import { Button } from "@/components/shadcn/button";
import { Switch } from "@/components/shadcn/switch";
import { EmailOptionsHelp } from "./components/EmailOptionsHelp";
import { EmailTaskRuleStep } from "./components/EmailTaskRuleStep";
import { WorkflowStep } from "./components/WorkflowStep";
import { WorkflowSummary } from "./components/WorkflowSummary";
import { useEmailOptions } from "./hooks/useEmailOptions";

/**
 * The Email Options page: what happens after an email is filed against a customer, laid out as
 * a top-to-bottom workflow so each choice reads as the next step of the one before.
 */
export const EmailOptionsPage = () => {
  const { options, draft, isLoading, loadError, isDirty, isSaving, setAutoCreateTask, updateRule, discard, save } =
    useEmailOptions();

  return (
    <MaintenanceSection>
      {/* Settings read as a form rather than a data grid, so the page is kept
          narrower than the full-width maintenance screens. */}
      <div className="max-w-4xl">
        <MaintenanceSectionHeader
          title="Email Options"
          subtitle="Decide what happens after an email is filed against a customer."
        />
        <MaintenanceSectionContent>
          {isLoading ? (
            <LoadingPage message="Loading email options..." />
          ) : loadError || !options || !draft ? (
            <Alert variant="error" persistent>
              {loadError ?? "We couldn't load the email options."}
            </Alert>
          ) : (
            <div className="grid gap-6 py-3">
              <ol>
                <WorkflowStep
                  icon={Mail}
                  title="An email is filed"
                  description="An email synced from a connected mailbox, or sent to your Klipboard CRM filing address, is filed against the contacts and companies it involves."
                />
                <WorkflowStep
                  icon={ListTodo}
                  title="Create a task from the email"
                  description="Track follow-ups by creating a task against the customer the email is filed under."
                  inactive={!draft.autoCreateTask}
                  action={
                    <Switch
                      checked={draft.autoCreateTask}
                      onCheckedChange={setAutoCreateTask}
                      aria-label="Create a task from each filed email"
                    />
                  }
                />
                <EmailTaskRuleStep
                  direction="inbound"
                  rule={draft.inbound}
                  activities={options.activities}
                  dueDayOptions={options.dueDayOptions}
                  enabled={draft.autoCreateTask}
                  onChange={changes => updateRule("inbound", changes)}
                />
                <EmailTaskRuleStep
                  direction="outbound"
                  rule={draft.outbound}
                  activities={options.activities}
                  dueDayOptions={options.dueDayOptions}
                  enabled={draft.autoCreateTask}
                  onChange={changes => updateRule("outbound", changes)}
                />
                <WorkflowStep icon={Flag} title="What happens" tone="outcome" isLast>
                  <WorkflowSummary settings={draft} activities={options.activities} />
                </WorkflowStep>
              </ol>

              <EmailOptionsHelp />

              <div className="sticky bottom-0 z-20 flex items-center justify-end gap-2 border-t bg-background/95 py-3 backdrop-blur">
                {isDirty && <span className="mr-auto text-sm text-muted-foreground">You have unsaved changes.</span>}
                <Button variant="outline" onClick={discard} disabled={!isDirty || isSaving}>
                  Discard
                </Button>
                <Button onClick={() => void save()} disabled={!isDirty || isSaving}>
                  {isSaving ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </div>
          )}
        </MaintenanceSectionContent>
      </div>
    </MaintenanceSection>
  );
};
